import React from 'react'

import { Flex, theme, useMediaQuery } from '@guiker/components-library'
import { useTranslation } from '@guiker/i18n'
import { CommercialIcon, ResidentialIcon, SquareFootageIcon, UnitsIcon } from '@guiker/icons'
import { Property } from '@guiker/real-estate-shared'

import { showBuildingOrientation, showTotalNumberOfUnits, showTotalSquareFootage } from '../../utils'
import { IconDescription } from './iconDescription'
import { findBuildingIcon, hasTwoOrMoreAttributes } from './utils'

type WholeBuildingConfigProp = {
  property: Property
}

export const WholeBuildingConfig: React.FC<WholeBuildingConfigProp> = ({ property }) => {
  const { t } = useTranslation('common-realEstate')

  const buildingType = property.building?.type
  const BuildingIcon = findBuildingIcon(buildingType)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const showSeperatedUnits = property.repartitionOfUnits?.residential > 0 && property.repartitionOfUnits?.commercial > 0

  if (!hasTwoOrMoreAttributes(property)) return <></>

  return (
    <Flex
      padding={2}
      flexDirection={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'flex-start' : 'center'}
      justifyContent={'space-evenly'}
      width='100%'
      flexWrap='wrap'
      gap={3}
    >
      {buildingType && (
        <IconDescription
          icon={<BuildingIcon />}
          description={t(`unitConfiguration.building.${buildingType}`)}
          value={''}
        />
      )}
      {showSeperatedUnits ? (
        <>
          <IconDescription
            icon={<ResidentialIcon />}
            description={t('unitConfiguration.residential', { count: property.repartitionOfUnits?.residential })}
            value={property.repartitionOfUnits?.residential?.toLocaleString()}
          />
          <IconDescription
            icon={<CommercialIcon />}
            description={t('unitConfiguration.commercial', { count: property.repartitionOfUnits?.commercial })}
            value={property.repartitionOfUnits?.commercial?.toLocaleString()}
          />
        </>
      ) : showTotalNumberOfUnits(property) ? (
        <IconDescription
          icon={<UnitsIcon />}
          description={t('unitConfiguration.unit', { count: property.building.configuration?.totalNumberOfUnits })}
          value={property.building.configuration?.totalNumberOfUnits?.toLocaleString()}
        />
      ) : undefined}
      {showTotalSquareFootage(property) && (
        <IconDescription
          icon={<SquareFootageIcon />}
          description={t('unitConfiguration.squareFootage')}
          value={property.totalSquareFootage?.toLocaleString()}
        />
      )}
      {showBuildingOrientation(property.building) && (
        <IconDescription
          description={t('unitConfiguration.orientation')}
          value={t(`unitConfiguration.${property.building.configuration?.orientation}`)}
        />
      )}
    </Flex>
  )
}
